import * as React from 'react';
import styled from 'styled-components';

import {
  Row,
  Input,
  Textarea,
  CollapsibleSection,
  Button,
  Img,
  ErrorBox,
  Title,
  Select,
  ExplorePageFields,
} from 'common/components';
import {
  FieldsGrid,
  GridItem,
  SaveButton,
  Section,
  SectionsWrapper,
} from 'templates/core';
import { BlogMobilePreview } from './BlogMobilePreview/BlogMobilePreview';
import ImagePlaceholder from './BlogMobilePreview/ImagePlaceholder';
import { BlogFields, fields } from 'modules/blogs/consts';
import { PK } from 'common/types';
import { getIsFriesland } from 'common/utils/config';
import BlogImagePreview from './BlogMobilePreview/BlogImagePreview';
import { STATUSES_OPTIONS } from '../../../common/consts';
import BlogTagsPicker from './BlogTagsPicker';
import { StandardError } from '../../../base/api/errors';
import { useWatch } from 'react-hook-form';

type Props = {
  pk?: PK;
  onSubmit: () => void;
  handleCheckClick: () => void;
  isSubmitting: boolean;
  defaultValues?: BlogFields;
  disabled: boolean;
  blogHtml: string;
  imageUrl?: string;
  error?: StandardError | null;
  layout?: string;
  created?: PK;
};

const BlogPickerForm = ({
  onSubmit,
  isSubmitting,
  handleCheckClick,
  defaultValues,
  blogHtml,
  disabled,
  imageUrl,
  error,
  pk,
  layout,
  created,
}: Props) => {
  const isFriesland = getIsFriesland();
  const image = imageUrl || defaultValues?.image_url;
  const titleValue = useWatch<BlogFields[]>({ name: fields.title.name });

  return (
    <>
      <form onSubmit={onSubmit}>
        <Section>
          <GridItem>
            <Title size="big">{layout === 'creation' ? 'Create Blog' : titleValue}</Title>
          </GridItem>
        </Section>
        <Section>
          <FieldsGrid>
            <GridItem compact>
              <Select
                name={fields.status.name}
                options={STATUSES_OPTIONS}
                readOnly={!created}
              />
            </GridItem>
            <GridItem>
              <Row>
                <Input name={fields.url.name} label={fields.url.label} />
                <StyledButton width={13} onClick={handleCheckClick}>
                  Get Data
                </StyledButton>
              </Row>
            </GridItem>
            <GridItem>
              <Input
                name={fields.title.name}
                label={fields.title.label}
                disabled={disabled}
              />
            </GridItem>
            <GridItem>
              <Textarea
                name={fields.description.name}
                label={fields.description.label}
                disabled={disabled}
              />
            </GridItem>
            <GridItem>
              <Row>
                <Input
                  name={fields.image_url.name}
                  label={fields.image_url.label}
                  disabled={disabled}
                />
              </Row>
            </GridItem>
            <GridItem>
              <GridItem>
                <BlogImagePreview>
                  {image ? (
                    <StyledImg src={image} alt="Blog image" />
                  ) : (
                    <ImagePlaceholder />
                  )}
                </BlogImagePreview>
              </GridItem>
            </GridItem>
            {isFriesland && (
              <GridItem>
                <CollapsibleSection title="Show preview" disabled={!pk || disabled}>
                  <BlogMobilePreview imageUrl={image} pageHtml={blogHtml} />
                </CollapsibleSection>
              </GridItem>
            )}
          </FieldsGrid>
        </Section>
        <ExplorePageFields fields={fields} />
        {error && (
          <Section>
            <ErrorBox error={error} />
          </Section>
        )}
        <SaveButton isLoading={isSubmitting} />
      </form>
      <SectionsWrapper pk={created}>
        <BlogTagsPicker />
      </SectionsWrapper>
    </>
  );
};

const StyledImg = styled(Img)`
  max-height: 240px;
  object-fit: contain;
`;

const StyledButton = styled(Button)`
  margin-left: ${({ theme }) => theme.spacer.medium};
`;

export default BlogPickerForm;
